/**
 * Table tennis ITTF rally scoring — deterministic state machine (Casual Arena).
 * scoringSystem: "rally"
 *
 * Official Laws of Table Tennis:
 * - Singles: no service-court L/R from score parity (unlike badminton/tennis)
 * - Doubles: always diagonal right → right
 * - Service by serveCount only (not by who won the rally)
 * - Deciding game: change ends at 5
 */

import {
  wrapSerializedMatch,
  unwrapSerializedMatch,
} from "../../contracts/MatchEngine";
import { playersFromTeam, setsToWinToBestOf } from "../../../matchScoringBridge";
import { cloneMatchSnapshot } from "../../shared/cloneMatchSnapshot";
import {
  advanceService,
  applyDoublesHit,
  deuceThreshold,
  deriveDoublesCourtLayout,
  endsChangeThreshold,
  initialDoublesRoles,
  isDecidingGame,
  isDeuce,
  migrateGameState,
  normalizePairOrder,
  resolveServeInterval,
  scoreMetaForGame,
  shouldChangeEnds,
  validateDoublesHit,
} from "./ttRules";

export const TABLE_TENNIS_ENGINE_ID = "table_tennis.ittf.v1";
export const TABLE_TENNIS_SCORING_SYSTEM = "rally";
/** v4: doubles serving/receiving order; court L/R derived each serve (ADR-033) */
const SCHEMA_VERSION = 4;

export const TABLE_TENNIS_FAULT_TYPES = Object.freeze([
  { id: "service_fault", label: "Service Fault" },
  { id: "hidden_serve", label: "Hidden Serve" },
  { id: "net_serve_let", label: "Net Serve (Let)" },
  { id: "double_bounce", label: "Double Bounce" },
  { id: "out", label: "Out" },
  { id: "net", label: "Net" },
  { id: "wrong_receiver", label: "Wrong Receiver" },
  { id: "illegal_sequence", label: "Illegal Hit Sequence" },
  { id: "other", label: "Other" },
]);

function clonePlayers(list) {
  return (list || []).map((p) => ({ ...p }));
}

function otherSide(side) {
  return side === "A" ? "B" : "A";
}

function pushUndo(match, snapshot) {
  return {
    ...match,
    undoStack: [...(match.undoStack || []), snapshot].slice(-50),
  };
}

function createGame(servingSide, pointsToWin, winBy, mode, orders = {}) {
  const pt = Math.max(1, pointsToWin);
  const base = {
    sideA: { score: 0 },
    sideB: { score: 0 },
    servingSide,
    serverIndex: 0,
    receiverIndex: 0,
    pointsToWin: pt,
    winBy,
    gameWinner: null,
    serveCount: 0,
    serveInterval: resolveServeInterval(pt, 0, 0),
    deuceThreshold: deuceThreshold(pt),
    firstServerSide: servingSide,
    currentServer: { side: servingSide, index: 0 },
    currentReceiver: {
      side: servingSide === "A" ? "B" : "A",
      index: 0,
    },
    lastPlayerHit: null,
    lastHitIndexBySide: {},
  };

  if (mode === "doubles") {
    const servingOrder = normalizePairOrder(
      orders.servingOrder ?? (servingSide === "A" ? orders.orderA : orders.orderB)
    );
    const receivingOrder = normalizePairOrder(
      orders.receivingOrder ?? (servingSide === "A" ? orders.orderB : orders.orderA)
    );
    const roles = initialDoublesRoles(servingSide, servingOrder, receivingOrder);
    return {
      ...base,
      orderA: roles.orderA,
      orderB: roles.orderB,
      servingSide: roles.currentServer.side,
      serverIndex: roles.currentServer.index,
      receiverIndex: roles.currentReceiver.index,
      currentServer: roles.currentServer,
      currentReceiver: roles.currentReceiver,
    };
  }

  return base;
}

function gamesWon(match, side) {
  return (match.games || []).filter((g) => g.gameWinner === side).length;
}

function matchWinnerFrom(match) {
  const need = Math.ceil((match.bestOf || 5) / 2);
  if (gamesWon(match, "A") >= need) return "A";
  if (gamesWon(match, "B") >= need) return "B";
  return null;
}

function finalizeGame(match, game, snapshot) {
  if (!game.gameWinner) {
    let nextMatch = { ...match, activeGame: game };
    // Deciding game: switch ends when first reaches 5 (ITTF).
    if (shouldChangeEnds(match, game) && !match.endsChangeDone) {
      nextMatch = {
        ...nextMatch,
        endsSwapped: !match.endsSwapped,
        endsChangeDone: true,
        awaitingEndsChange: false,
      };
    }
    return pushUndo(nextMatch, snapshot);
  }
  const games = [...(match.games || []), game];
  const provisional = {
    ...match,
    games,
    activeGame: null,
    awaitingEndsChange: false,
    endsChangeDone: false,
  };
  const winner = matchWinnerFrom(provisional);
  if (winner) {
    return pushUndo(
      { ...provisional, matchWinner: winner, awaitingNextGame: false },
      snapshot
    );
  }
  // ITTF: player who served first in previous game receives first in the next.
  const nextFirst = game.firstServerSide
    ? otherSide(game.firstServerSide)
    : otherSide(game.servingSide);
  return pushUndo(
    {
      ...provisional,
      matchWinner: null,
      awaitingNextGame: true,
      endsSwapped: !match.endsSwapped,
      pendingNextServer: nextFirst,
    },
    snapshot
  );
}

function awardPoint(match, side) {
  const g = match.activeGame;
  if (!g || g.gameWinner) return { match, game: g };
  const next = {
    ...g,
    sideA: { score: g.sideA.score + (side === "A" ? 1 : 0) },
    sideB: { score: g.sideB.score + (side === "B" ? 1 : 0) },
  };
  const a = next.sideA.score;
  const b = next.sideB.score;
  let withServe = advanceService(next, match.mode || "singles");
  if (
    (a >= withServe.pointsToWin || b >= withServe.pointsToWin) &&
    Math.abs(a - b) >= withServe.winBy
  ) {
    withServe = { ...withServe, gameWinner: a > b ? "A" : "B" };
  }
  return { match: { ...match, activeGame: withServe }, game: withServe };
}

function migrateMatchState(state) {
  if (!state || typeof state !== "object") return state;
  const mode = state.mode || "singles";
  return {
    ...state,
    activeGame: state.activeGame ? migrateGameState(state.activeGame, mode) : null,
    games: (state.games || []).map((g) => migrateGameState(g, mode)),
    awaitingEndsChange: Boolean(state.awaitingEndsChange),
    endsChangeDone: Boolean(state.endsChangeDone),
  };
}

/**
 * Derive [right, left] slot order from current server/receiver — never persist L/R.
 * Uses match.sideAPlayers / sideBPlayers as canonical index 0/1 (not already-synced slots).
 */
function syncTtDoublesToBoxes(teamA, teamB, match) {
  const g = match?.activeGame;
  if (!g || match?.mode !== "doubles") return { teamA, teamB };

  const layout = deriveDoublesCourtLayout(g.currentServer, g.currentReceiver);

  const orderSide = (team, sidePlayers, sideLayout) => {
    const slots = team?.slots;
    if (!slots || slots.length < 2 || !sideLayout) return team;
    const canon = sidePlayers?.length >= 2 ? sidePlayers : null;

    const resolveSlot = (playerIndex) => {
      if (canon) {
        const p = canon[playerIndex];
        if (p) {
          const id = p.playerId || p.registryEntryId || p.id;
          const found = slots.find(
            (s) =>
              (s.playerId && s.playerId === p.playerId) ||
              (s.registryEntryId && s.registryEntryId === (p.registryEntryId || p.id)) ||
              (id && (s.playerId === id || s.registryEntryId === id))
          );
          if (found) return found;
        }
      }
      return slots[playerIndex];
    };

    const right = resolveSlot(sideLayout.right);
    const left = resolveSlot(sideLayout.left);
    if (!right || !left) return team;
    return { ...team, slots: [right, left] };
  };

  return {
    teamA: orderSide(teamA, match.sideAPlayers, layout.A),
    teamB: orderSide(teamB, match.sideBPlayers, layout.B),
  };
}

/**
 * @returns {import("../../contracts/MatchEngine").MatchEngine}
 */
export function createTableTennisMatchEngine() {
  const engine = {
    engineId: TABLE_TENNIS_ENGINE_ID,
    scoringSystem: TABLE_TENNIS_SCORING_SYSTEM,

    startMatch(config) {
      const mode = config.teamA?.type === "doubles" ? "doubles" : "singles";
      const pointsToWin = Math.max(1, config.pointsToWin ?? 11);
      const servingSide = config.firstServerSide || "A";
      const game = createGame(servingSide, pointsToWin, 2, mode, {
        servingOrder: config.doublesServingOrder,
        receivingOrder: config.doublesReceivingOrder,
        orderA: config.orderA,
        orderB: config.orderB,
      });
      return {
        engineId: TABLE_TENNIS_ENGINE_ID,
        scoringSystem: TABLE_TENNIS_SCORING_SYSTEM,
        bestOf: setsToWinToBestOf(config.setsToWin ?? 3),
        mode,
        endsSwapped: !!config.endsSwapped,
        sideAPlayers: clonePlayers(playersFromTeam(config.teamA)),
        sideBPlayers: clonePlayers(playersFromTeam(config.teamB)),
        games: [],
        activeGame: game,
        matchWinner: null,
        awaitingNextGame: false,
        awaitingEndsChange: false,
        endsChangeDone: false,
        undoStack: [],
      };
    },

    startNextGame(match, config) {
      if (!match?.games?.length) {
        throw new Error("startNextGame: no completed games");
      }
      const mode = match.mode || "singles";
      const pointsToWin = Math.max(1, config.pointsToWin ?? 11);
      const servingSide =
        match.pendingNextServer || config.firstServerSide || "A";
      const prev = match.games[match.games.length - 1];
      const game = createGame(servingSide, pointsToWin, 2, mode, {
        servingOrder: config.doublesServingOrder,
        receivingOrder: config.doublesReceivingOrder,
        orderA: prev?.orderA || config.orderA,
        orderB: prev?.orderB || config.orderB,
      });
      const { awaitingNextGame: _a, pendingNextServer: _p, ...rest } = match;
      return {
        ...rest,
        endsSwapped: !!config.endsSwapped,
        activeGame: game,
        awaitingNextGame: false,
        awaitingEndsChange: false,
        endsChangeDone: false,
        pendingNextServer: null,
        matchWinner: null,
        undoStack: [],
      };
    },

    scorePoint(match, side) {
      if (!match?.activeGame || match.matchWinner) return match;
      const snapshot = cloneMatchSnapshot(match);
      const { match: nextMatch, game } = awardPoint(match, side);
      return finalizeGame(nextMatch, game, snapshot);
    },

    fault(match, faultType, side) {
      if (!match?.activeGame || match.matchWinner) return match;
      const type = faultType || "other";
      if (type === "net_serve_let") {
        return match; // let — replay
      }
      const snapshot = cloneMatchSnapshot(match);
      const faulting = side || match.activeGame.servingSide;
      const { match: nextMatch, game } = awardPoint(match, otherSide(faulting));
      return finalizeGame(nextMatch, game, snapshot);
    },

    /**
     * Doubles rally contact. Same-team consecutive or non-alternating partner
     * immediately awards the point to the opponent.
     */
    recordHit(match, side, index = 0) {
      if (!match?.activeGame || match.matchWinner) return match;
      if (match.mode !== "doubles") return match;
      const g = match.activeGame;
      const check = validateDoublesHit(g, side, index);
      if (!check.ok) {
        const snapshot = cloneMatchSnapshot(match);
        const { match: nextMatch, game } = awardPoint(match, otherSide(side));
        return finalizeGame(nextMatch, game, snapshot);
      }
      const snapshot = cloneMatchSnapshot(match);
      return pushUndo(
        { ...match, activeGame: applyDoublesHit(g, side, index) },
        snapshot
      );
    },

    confirmEndsChange(match) {
      if (!match?.activeGame || match.endsChangeDone) return match;
      if (!shouldChangeEnds(match, match.activeGame) && !match.awaitingEndsChange) {
        return match;
      }
      const snapshot = cloneMatchSnapshot(match);
      return pushUndo(
        {
          ...match,
          endsSwapped: !match.endsSwapped,
          awaitingEndsChange: false,
          endsChangeDone: true,
        },
        snapshot
      );
    },

    undo(match) {
      if (!match?.undoStack?.length) return match;
      return match.undoStack[match.undoStack.length - 1];
    },

    canUndo(match) {
      return Boolean(match?.undoStack?.length);
    },

    getState(match) {
      return match || null;
    },

    getLiveView(match) {
      if (!match) {
        return {
          scoreA: 0,
          scoreB: 0,
          serving: "A",
          sets: [],
          winner: null,
          endsSwapped: false,
          serveDisplay: null,
          extras: null,
        };
      }
      const g = match.activeGame;
      const mode = match.mode || "singles";
      const sets = (match.games || []).map((x) => ({
        scoreA: x.sideA.score,
        scoreB: x.sideB.score,
        winner: x.gameWinner,
      }));
      const a = g?.sideA.score ?? 0;
      const b = g?.sideB.score ?? 0;
      const deuce = g ? isDeuce(a, b, g.pointsToWin) : false;
      const gameNum = (match.games || []).length + 1;
      const interval = g ? resolveServeInterval(g.pointsToWin, a, b) : 2;
      const deciding = isDecidingGame(match);
      const endsChanged =
        Boolean(match.endsChangeDone) &&
        deciding &&
        g &&
        Math.max(a, b) >= endsChangeThreshold(g.pointsToWin);

      return {
        scoreA: a,
        scoreB: b,
        serving: g?.servingSide ?? "A",
        sets,
        winner: match.matchWinner,
        endsSwapped: !!match.endsSwapped,
        serveDisplay: g ? `${a}–${b}` : null,
        extras: g
          ? {
              mode,
              serverIndex: g.currentServer?.index ?? g.serverIndex ?? 0,
              receiverIndex: g.currentReceiver?.index ?? g.receiverIndex ?? 0,
              currentServer: g.currentServer,
              currentReceiver: g.currentReceiver,
              /** Derived each serve — do not treat as persistent player attributes */
              courtLayout:
                mode === "doubles"
                  ? deriveDoublesCourtLayout(g.currentServer, g.currentReceiver)
                  : null,
              serveCount: g.serveCount ?? 0,
              serveInterval: interval,
              /** Singles: no service court. Doubles: always right→right diagonal. */
              serveCourt: mode === "doubles" ? "right" : null,
              serveDiagonal: mode === "doubles",
              deuce,
              decidingGame: deciding,
              endsChangeDone: Boolean(match.endsChangeDone),
              endsChanged,
              endsChangeThreshold: endsChangeThreshold(g.pointsToWin),
              orderA: g.orderA || null,
              orderB: g.orderB || null,
              phaseLabel: `GAME ${gameNum}`,
              scoreMeta: endsChanged
                ? `${scoreMetaForGame(g, mode)} · Ends changed`
                : scoreMetaForGame(g, mode),
            }
          : null,
      };
    },

    getWinner(match) {
      return match?.matchWinner ?? null;
    },

    serialize(match) {
      if (!match) return null;
      return wrapSerializedMatch(TABLE_TENNIS_ENGINE_ID, SCHEMA_VERSION, match);
    },

    deserialize(json) {
      const unwrapped = unwrapSerializedMatch(json);
      if (!unwrapped.state || unwrapped.legacy) return null;
      if (unwrapped.engineId && unwrapped.engineId !== TABLE_TENNIS_ENGINE_ID) {
        return null;
      }
      return migrateMatchState(unwrapped.state);
    },

    isGamePoint(match) {
      const g = match?.activeGame;
      if (!g) return false;
      const a = g.sideA.score;
      const b = g.sideB.score;
      return (
        (a + 1 >= g.pointsToWin && a + 1 - b >= g.winBy) ||
        (b + 1 >= g.pointsToWin && b + 1 - a >= g.winBy)
      );
    },

    isMatchPoint(match) {
      if (!this.isGamePoint(match)) return false;
      const need = Math.ceil((match.bestOf || 5) / 2);
      const g = match.activeGame;
      const leader = g.sideA.score >= g.sideB.score ? "A" : "B";
      return gamesWon(match, leader) + 1 >= need;
    },

    hasInterval() {
      return false;
    },

    resumeInterval(match) {
      return match;
    },

    isAwaitingCourtSetup(match) {
      return Boolean(match?.awaitingNextGame);
    },

    suggestCourtSetup(match) {
      return {
        suggestedServerSide: match?.pendingNextServer || "A",
        endsSwapped: !match?.endsSwapped,
      };
    },

    syncTeamsToBoxes(teamA, teamB, match) {
      return syncTtDoublesToBoxes(teamA, teamB, match);
    },

    listFaultTypes() {
      return [...TABLE_TENNIS_FAULT_TYPES];
    },

    setsToWinToBestOf,
  };

  return {
    ...engine,
    initScoringMatch(teamA, teamB, setsToWin, endsSwapped, firstServerSide, pointsToWin) {
      return engine.startMatch({
        teamA,
        teamB,
        setsToWin,
        endsSwapped,
        firstServerSide,
        pointsToWin,
      });
    },
    startNextGameFromCourtSetup(existing, teamA, teamB, endsSwapped, firstServerSide, pointsToWin) {
      return engine.startNextGame(existing, {
        teamA,
        teamB,
        endsSwapped,
        firstServerSide,
        pointsToWin,
      });
    },
    matchToLiveView: engine.getLiveView,
    serializeScoringMatch: engine.serialize,
    deserializeScoringMatch: engine.deserialize,
    recordRally: engine.scorePoint,
    undoLastRally: engine.undo,
    canUndoRally: engine.canUndo,
    resumeFromInterval: engine.resumeInterval,
    suggestCourtSetupAfterGame: engine.suggestCourtSetup,
    syncTeamsToActiveGameBoxes: engine.syncTeamsToBoxes,
    confirmEndsChange: engine.confirmEndsChange,
    recordHit: engine.recordHit,
  };
}
